'use client';

import { LeadsPublicApi, ProfileTheme } from '@up2stay/up2promo-api-client';
import { AnimatePresence, motion } from 'motion/react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import de from 'react-phone-number-input/locale/de';
import en from 'react-phone-number-input/locale/en';
import { toast } from 'sonner';

import { Button } from '../components/button';
import { Checkbox } from '../components/checkbox';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from '../components/drawer';
import { Input } from '../components/input';
import { Label } from '../components/label';
import { PhoneInput } from '../components/phone-input';
import { cn } from '../utils/cn';
import { markLeadFormSubmitted } from './lib/lead-form-storage';
import {
  getProfileAccentCssVars,
  resolveProfileThemePreference,
  type ProfileTokenPalette,
} from './profile-accent-color';
import {
  ProfileThemeRoot,
  type ProfileThemeMode,
} from './profile-accent-theme';
// Dictionary type for translations (optional) - import from public-profile-content
import type { Dictionary } from './public-profile-content';

const PRIVACY_POLICY_URL = 'https://www.up2promo.com/policies/privacy-policy';

interface PublicLeadFormProps {
  profileId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isPreview?: boolean;
  dictionary?: Dictionary;
  /** Profile secondary/accent color; defaults to brand blue when unset. */
  accentColor?: string | null;
  /** Match the public profile theme (drawer portals out of ProfileThemeRoot). */
  theme?: ProfileThemeMode | null;
  palette?: ProfileTokenPalette;
}

/**
 * Theme tokens for the exchange sheet. Uses elevated `--card` / surface as the
 * sheet fill so it matches form controls (`bg-card`), not the page canvas.
 */
function useLeadFormSheetTheme(
  accentColor: string | null | undefined,
  theme: ProfileThemeMode | null | undefined,
  palette: ProfileTokenPalette,
): { style: CSSProperties; isDark: boolean } {
  const [prefersDark, setPrefersDark] = useState(true);

  useEffect(() => {
    if (theme !== ProfileTheme.AUTO || typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const update = () => setPrefersDark(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, [theme]);

  const resolvedTheme = useMemo(
    () => resolveProfileThemePreference(theme, prefersDark),
    [theme, prefersDark],
  );

  const style = useMemo(() => {
    const vars = getProfileAccentCssVars(accentColor, {
      theme: resolvedTheme,
      palette,
    }) as CSSProperties & Record<string, string>;
    return {
      ...vars,
      // Align sheet chrome with formControlSurfaceClass (`bg-card`).
      background: vars['--card'] ?? vars.background,
    };
  }, [accentColor, resolvedTheme, palette]);

  return { style, isDark: resolvedTheme === 'dark' };
}

/** Client-side email format check to match backend @IsEmail() and avoid submit-then-API-error. */
function isEmailValid(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  // Reasonable pattern aligned with common IsEmail behavior (local@domain.tld)
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(trimmed);
}

function renderConsentWithPrivacyLink(consentText: string) {
  const match = consentText.match(/Daten|data/i);
  if (!match || match.index == null) return consentText;

  const keyword = match[0];
  const i = match.index;
  const before = consentText.slice(0, i);
  const after = consentText.slice(i + keyword.length);

  return (
    <>
      {before}
      <a
        href={PRIVACY_POLICY_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="underline underline-offset-4 hover:text-foreground transition-colors"
      >
        {keyword}
      </a>
      {after}
    </>
  );
}

/** Centered success card with blur backdrop and animated checkmark */
function SuccessConfirmationCard({
  title,
  description,
  visible,
  onDismiss,
  className,
}: {
  title: string;
  description: string;
  visible: boolean;
  onDismiss: () => void;
  className?: string;
}) {
  const circleLength = 2 * Math.PI * 24;
  const checkPath = 'M 20 32 L 28 40 L 44 22';
  const checkLength = 34;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          onClick={onDismiss}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="absolute inset-0 bg-black/40 backdrop-blur-md"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', damping: 26, stiffness: 240 }}
            onClick={(e) => e.stopPropagation()}
            className={cn(
              'relative z-10 flex w-[min(320px,90vw)] flex-col items-center justify-center rounded-lg border border-border/80 bg-card p-8 text-center shadow-2xl',
              'h-[min(320px,90vw)] max-h-[85vh]',
              className,
            )}
          >
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center">
              <svg
                className="h-16 w-16 text-primary"
                viewBox="0 0 64 64"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <motion.circle
                  cx="32"
                  cy="32"
                  r="24"
                  fill="none"
                  strokeDasharray={circleLength}
                  initial={{ strokeDashoffset: circleLength }}
                  animate={{ strokeDashoffset: 0 }}
                  transition={{ delay: 0.15, duration: 0.55, ease: 'easeOut' }}
                />
                <motion.path
                  d={checkPath}
                  strokeDasharray={checkLength}
                  initial={{ strokeDashoffset: checkLength }}
                  animate={{ strokeDashoffset: 0 }}
                  transition={{ delay: 0.5, duration: 0.45, ease: 'easeOut' }}
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold tracking-tight text-foreground">
              {title}
            </h3>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              {description}
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const FORM_COPY = {
  de: {
    title: 'Kontakt austauschen',
    description: 'Fülle das Formular unten aus und wir melden uns bei dir',
    firstName: 'Vorname',
    firstNamePlaceholder: 'Gib deinen Vornamen ein',
    lastName: 'Nachname',
    lastNamePlaceholder: 'Gib deinen Nachnamen ein',
    email: 'E-Mail',
    emailPlaceholder: 'Gib deine E-Mail ein',
    phone: 'Telefon',
    phonePlaceholder: 'Telefonnummer',
    workspace: 'Unternehmen',
    workspacePlaceholder: 'Gib dein Unternehmen ein',
    consent:
      'Ich stimme zu, dass meine Daten verwendet werden, um mich zu kontaktieren',
    cancel: 'Abbrechen',
    submit: 'Absenden',
    submitting: 'Wird gesendet...',
    firstNameRequired: 'Vorname ist erforderlich',
    emailRequired: 'E-Mail ist erforderlich',
    emailInvalid: 'Bitte gib eine gültige E-Mail-Adresse ein',
    phoneRequired: 'Telefonnummer ist erforderlich',
    consentRequired: 'Bitte akzeptiere die Einwilligung, um fortzufahren',
    submitFailed:
      'Formular konnte nicht gesendet werden. Bitte versuche es erneut.',
  },
  en: {
    title: 'Get in Touch',
    description: "Fill out the form below and we'll get back to you",
    firstName: 'First name',
    firstNamePlaceholder: 'Enter your first name',
    lastName: 'Last name',
    lastNamePlaceholder: 'Enter your last name',
    email: 'Email',
    emailPlaceholder: 'Enter your email',
    phone: 'Phone',
    phonePlaceholder: 'Phone number',
    workspace: 'Company',
    workspacePlaceholder: 'Enter your company',
    consent: 'I agree that my data will be used to contact me back',
    cancel: 'Cancel',
    submit: 'Submit',
    submitting: 'Submitting...',
    firstNameRequired: 'First name is required',
    emailRequired: 'Email is required',
    emailInvalid: 'Please enter a valid email address',
    phoneRequired: 'Phone number is required',
    consentRequired: 'Please accept the consent to continue',
    submitFailed: 'Failed to submit form. Please try again.',
  },
} as const;

type FormCopyKey = keyof typeof FORM_COPY.de;

type LeadFormField = 'firstName' | 'email' | 'phone' | 'consentAccepted';

function resolveFormLocale(
  form: Record<string, unknown> | undefined,
): 'de' | 'en' {
  const title = form?.title;
  const submit = form?.submit;
  if (typeof title === 'string' && /get in touch/i.test(title)) return 'en';
  if (typeof submit === 'string' && /^submit$/i.test(submit.trim()))
    return 'en';
  return 'de';
}

export function PublicLeadForm({
  profileId,
  open,
  onOpenChange,
  isPreview = false,
  dictionary,
  accentColor,
  theme = ProfileTheme.DARK,
  palette = 'standard',
}: PublicLeadFormProps) {
  const dict = dictionary || { leads: { form: {} } };
  const formDict = (dict.leads?.form ?? {}) as Record<string, unknown>;
  const formLocale = resolveFormLocale(formDict);
  const copy = FORM_COPY[formLocale];
  const phoneLabels = formLocale === 'en' ? en : de;
  const sheetTheme = useLeadFormSheetTheme(accentColor, theme, palette);

  // Type-safe dictionary accessors with locale-matched fallbacks
  const getFormValue = (key: string, fallback = ''): string => {
    const value = formDict[key];
    if (typeof value === 'string' && value.trim() !== '') return value;
    if (key in copy) return copy[key as FormCopyKey];
    return fallback;
  };

  const getSuccessValue = (key: string, fallback = ''): string => {
    const success = formDict.success as Record<string, unknown> | undefined;
    const value = success?.[key];
    return typeof value === 'string' ? value : fallback;
  };

  const getErrorValue = (key: string, fallback: string): string => {
    const errors = formDict.errors as Record<string, unknown> | undefined;
    const value = errors?.[key];
    if (typeof value === 'string' && value.trim() !== '') return value;
    if (key in copy) return copy[key as FormCopyKey];
    return fallback;
  };

  const getSettingsValue = (key: string, fallback = ''): string => {
    const settings = (dict.settings as Record<string, unknown>) || {};
    const value = settings[key];
    if (typeof value === 'string' && value.trim() !== '') return value;
    if (key === 'cancel') return copy.cancel;
    return fallback;
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessCard, setShowSuccessCard] = useState(false);
  const [successMessage, setSuccessMessage] = useState<{
    title: string;
    description: string;
  }>({ title: '', description: '' });
  const showSuccessTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const isMountedRef = useRef(true);
  const [formData, setFormData] = useState<Record<string, string | boolean>>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    organization: '',
    consentAccepted: false,
  });

  // Inline, styled field errors instead of the browser's "Fülle dieses Feld
  // aus." bubble (form is noValidate, DEV-539).
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<LeadFormField, string>>
  >({});

  const setFieldValue = (key: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key as LeadFormField];
      return next;
    });
  };

  const collectErrors = (): Partial<Record<LeadFormField, string>> => {
    const errors: Partial<Record<LeadFormField, string>> = {};
    if (!String(formData.firstName ?? '').trim()) {
      errors.firstName = getErrorValue(
        'firstNameRequired',
        copy.firstNameRequired,
      );
    }
    const emailStr = String(formData.email ?? '').trim();
    if (!emailStr) {
      errors.email = getErrorValue('emailRequired', copy.emailRequired);
    } else if (!isEmailValid(emailStr)) {
      errors.email = getErrorValue('emailInvalid', copy.emailInvalid);
    }
    if (!String(formData.phone ?? '').trim()) {
      errors.phone = getErrorValue('phoneRequired', copy.phoneRequired);
    }
    if (!formData.consentAccepted) {
      errors.consentAccepted = getErrorValue(
        'consentRequired',
        copy.consentRequired,
      );
    }
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const errors = collectErrors();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      const form = e.currentTarget;
      requestAnimationFrame(() => {
        form.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
      });
      return;
    }
    const body = {
      firstName: String(formData.firstName ?? '').trim(),
      lastName: String(formData.lastName ?? '').trim() || null,
      email: String(formData.email ?? '').trim(),
      phone: String(formData.phone ?? '').trim(),
      companyName: String(formData.organization ?? '').trim() || null,
      consentAccepted: (formData.consentAccepted as boolean) ?? null,
    };

    try {
      setIsSubmitting(true);
      await LeadsPublicApi.submitLeadForm({
        path: { profileId },
        body,
      });
      markLeadFormSubmitted(profileId);
      const submittedEmail = (body.email as string)?.trim();
      const title = getSuccessValue('title', 'Thank you!');
      const description = submittedEmail
        ? [
            getSuccessValue('description', "We've received your message."),
            getSuccessValue(
              'vCardSent',
              'Contact details were sent to your email.',
            ),
          ]
            .filter(Boolean)
            .join(' ')
        : getSuccessValue(
            'description',
            "We've received your message and will get back to you soon.",
          );
      onOpenChange(false);
      resetForm();
      setSuccessMessage({ title, description });
      if (showSuccessTimeoutRef.current) {
        clearTimeout(showSuccessTimeoutRef.current);
      }
      // Wait for drawer close animation before showing the success card
      showSuccessTimeoutRef.current = setTimeout(() => {
        if (isMountedRef.current) setShowSuccessCard(true);
      }, 260);
    } catch (error) {
      console.error('Failed to submit lead form:', error);
      const errorMsg =
        (dict.leads?.form?.errors as { submitFailed?: string })?.submitFailed ||
        'Failed to submit';
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = useCallback(() => {
    setFieldErrors({});
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      organization: '',
      consentAccepted: false,
    });
  }, []);

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onOpenChange(false);
    }
  };

  const prevOpenRef = useRef(open);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      resetForm();
    }
    prevOpenRef.current = open;
  }, [open, resetForm]);

  useEffect(() => {
    if (!showSuccessCard) return;
    const t = setTimeout(() => setShowSuccessCard(false), 4000);
    return () => clearTimeout(t);
  }, [showSuccessCard]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (showSuccessTimeoutRef.current) {
        clearTimeout(showSuccessTimeoutRef.current);
        showSuccessTimeoutRef.current = null;
      }
    };
  }, []);

  const successCard = (
    <ProfileThemeRoot accentColor={accentColor} theme={theme} palette={palette}>
      <SuccessConfirmationCard
        title={successMessage.title}
        description={successMessage.description}
        visible={showSuccessCard}
        onDismiss={() => setShowSuccessCard(false)}
      />
    </ProfileThemeRoot>
  );

  const fieldError = (field: LeadFormField, inputId: string) =>
    fieldErrors[field] ? (
      <p
        id={`${inputId}-error`}
        role="alert"
        className="text-xs text-destructive"
      >
        {fieldErrors[field]}
      </p>
    ) : null;

  const errorProps = (field: LeadFormField, inputId: string) =>
    fieldErrors[field]
      ? {
          'aria-invalid': true as const,
          'aria-describedby': `${inputId}-error`,
        }
      : {};

  /** Fields shared by the in-app preview and the public drawer. */
  const renderFields = (idSuffix: string, phoneInternational: boolean) => {
    const id = (name: string) => `${name}${idSuffix}`;
    return (
      <>
        <div className="space-y-2">
          <Label htmlFor={id('firstName')}>
            {getFormValue('firstName')}{' '}
            <span className="text-destructive">*</span>
          </Label>
          <Input
            id={id('firstName')}
            autoComplete="given-name"
            value={(formData.firstName as string) ?? ''}
            onChange={(e) => setFieldValue('firstName', e.target.value)}
            placeholder={getFormValue('firstNamePlaceholder')}
            required
            {...errorProps('firstName', id('firstName'))}
          />
          {fieldError('firstName', id('firstName'))}
        </div>

        <div className="space-y-2">
          <Label htmlFor={id('lastName')}>{getFormValue('lastName')}</Label>
          <Input
            id={id('lastName')}
            autoComplete="family-name"
            value={(formData.lastName as string) ?? ''}
            onChange={(e) => setFieldValue('lastName', e.target.value)}
            placeholder={getFormValue('lastNamePlaceholder')}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor={id('organization')}>
            {getFormValue('workspace')}
          </Label>
          <Input
            id={id('organization')}
            autoComplete="organization"
            value={(formData.organization as string) ?? ''}
            onChange={(e) => setFieldValue('organization', e.target.value)}
            placeholder={getFormValue('workspacePlaceholder')}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor={id('email')}>
            {getFormValue('email')} <span className="text-destructive">*</span>
          </Label>
          <Input
            id={id('email')}
            type="email"
            autoComplete="email"
            value={(formData.email as string) ?? ''}
            onChange={(e) => setFieldValue('email', e.target.value)}
            placeholder={getFormValue('emailPlaceholder')}
            required
            {...errorProps('email', id('email'))}
          />
          {fieldError('email', id('email'))}
        </div>

        <div className="space-y-2">
          <Label htmlFor={id('phone')}>
            {getFormValue('phone')} <span className="text-destructive">*</span>
          </Label>
          <PhoneInput
            id={id('phone')}
            international={phoneInternational || undefined}
            required
            defaultCountry="DE"
            labels={phoneLabels}
            placeholder={getFormValue('phonePlaceholder')}
            value={(formData.phone as string) || undefined}
            onChange={(v) => setFieldValue('phone', v || '')}
            {...errorProps('phone', id('phone'))}
          />
          {fieldError('phone', id('phone'))}
        </div>

        <div className="space-y-2">
          <div className="flex items-start gap-2">
            <Checkbox
              id={id('consent')}
              checked={formData.consentAccepted as boolean}
              onCheckedChange={(checked) =>
                setFieldValue('consentAccepted', checked === true)
              }
              {...errorProps('consentAccepted', id('consent'))}
            />
            <Label
              htmlFor={id('consent')}
              className="text-sm leading-relaxed cursor-pointer"
            >
              {renderConsentWithPrivacyLink(getFormValue('consent'))}
            </Label>
          </div>
          {fieldError('consentAccepted', id('consent'))}
        </div>
      </>
    );
  };

  // In preview, render drawer without portal (stays within preview container)
  if (isPreview) {
    return (
      <>
        {successCard}
        <AnimatePresence>
          {open && (
            <div className="absolute inset-0 z-50 overflow-hidden">
              {/* Overlay */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="absolute inset-0 bg-black/80"
                onClick={handleClose}
              />
              {/* Drawer Content — theme on the sheet so fields match chrome */}
              <motion.div
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{
                  type: 'spring',
                  damping: 30,
                  stiffness: 300,
                }}
                style={sheetTheme.style}
                className={cn(
                  'absolute inset-x-0 bottom-0 z-10 flex h-auto max-h-[90vh] flex-col rounded-t-[10px] border border-[var(--border)] bg-card text-card-foreground shadow-lg',
                  sheetTheme.isDark && 'dark',
                )}
              >
                <div className="mx-auto mt-4 h-2 w-[100px] rounded-full bg-muted" />
                <div className="grid gap-1.5 px-4 pt-4 pb-2 text-center sm:text-left">
                  <h3 className="text-lg font-semibold leading-none tracking-tight">
                    {(dict.leads?.form?.title as string) || 'Contact'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {(dict.leads?.form?.description as string) || ''}
                  </p>
                </div>
                <div className="px-4 pb-4 overflow-y-auto flex-1">
                  <form
                    noValidate
                    onSubmit={handleSubmit}
                    className="space-y-4"
                  >
                    {renderFields('', true)}

                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={handleClose}
                        disabled={isSubmitting}
                        className="flex-1"
                      >
                        {getSettingsValue('cancel')}
                      </Button>
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex-1"
                      >
                        {isSubmitting
                          ? getFormValue('submitting')
                          : getFormValue('submit')}
                      </Button>
                    </div>
                  </form>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </>
    );
  }

  // Normal drawer version for real profile page
  return (
    <>
      {successCard}
      <Drawer open={open} onOpenChange={onOpenChange}>
        {/* Portal leaves ProfileThemeRoot — put theme vars on the sheet itself. */}
        <DrawerContent
          className={cn(
            'max-h-[min(90dvh,52rem)] border-[var(--border)] bg-card text-card-foreground',
            sheetTheme.isDark && 'dark',
          )}
          style={sheetTheme.style}
        >
          <div className="mx-auto w-full max-w-2xl">
            <div className="px-4 pt-4 pb-2">
              <DrawerTitle className="text-center sm:text-left text-lg font-semibold leading-none tracking-tight">
                {getFormValue('title')}
              </DrawerTitle>
              <DrawerDescription className="text-center sm:text-left text-sm text-muted-foreground mt-1.5">
                {getFormValue('description')}
              </DrawerDescription>
            </div>
            <form noValidate onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-4 px-4">
                {renderFields('-drawer', false)}
              </div>

              <div className="px-4 pb-4">
                <div className="mx-auto flex max-w-xl gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleClose}
                    disabled={isSubmitting}
                    className="flex-1"
                  >
                    {getSettingsValue('cancel')}
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1"
                  >
                    {isSubmitting
                      ? getFormValue('submitting')
                      : getFormValue('submit')}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
